import React, { useState, useEffect } from "react";
import { api } from "../../lib/apiClient";
import { Edit3, Plus, Trash2 } from "lucide-react";
import { Input, SimpleDialog } from "./_shared";
import { useConfirm } from "../../components/ConfirmDialog";

export function BranchesTab({ kind = "key_account" }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const isDepot = kind === "depot";
  const label = isDepot ? "Branch" : "Key Account Client";
  const confirm = useConfirm();

  const load = () =>
    api.get(`/branches?kind=${kind}`).then((r) => setItems(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kind]);

  const newOne = () =>
    setEditing({
      name: "",
      labour_rate: 0,
      default_markup_percent: 0,
      surcharge_amount: 0,
      min_repair_hours: null,
      key_account: !isDepot,
      show_labour_separate: false,
      kind,
    });

  const save = async () => {
    const body = { ...editing, kind, key_account: !isDepot };
    if (editing.id) await api.put(`/branches/${editing.id}`, body);
    else await api.post("/branches", body);
    setEditing(null); load();
  };

  const remove = async (id) => {
    const ok = await confirm({ title: `Delete this ${label.toLowerCase()}?`, confirmText: "Delete", destructive: true });
    if (!ok) return;
    await api.delete(`/branches/${id}`);
    load();
  };

  return (
    <div className="space-y-4">
      {isDepot && (
        <div className="text-xs text-slate-500 max-w-2xl">
          Branches control the depot basic labour rates (Plymouth &amp; Bristol). These rates are used when a quote isn't tied to a key account client.
          Set a <strong>Default Materials Markup %</strong> to auto-apply to every new Parts &amp; Materials line on quotes using this depot.
        </div>
      )}
      {!isDepot && (
        <div className="text-xs text-slate-500 max-w-2xl">
          Key account clients hold client-specific labour rates that override the basic branch rate when selected on a quote.
          Set a <strong>Default Materials Markup %</strong> to auto-apply to every new Parts &amp; Materials line on quotes using this client.
        </div>
      )}
      <button
        onClick={newOne}
        className="h-9 px-4 bg-brand-blue text-white text-xs uppercase tracking-wider font-semibold inline-flex items-center gap-2 hover:bg-brand-blue-dark"
        data-testid={isDepot ? "new-branch-button" : "new-key-account-button"}
      >
        <Plus size={14} /> Add {label}
      </button>
      <div className="border border-slate-200">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 border-b-2 border-slate-900">
              <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-600">Name</th>
              <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-600">Labour Rate £/hr</th>
              <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-600">Default Markup %</th>
              {!isDepot && (
                <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-600" title="Auto-applied to every new quote using this key account">Surcharge (£)</th>
              )}
              <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-600" title="Per-branch override of the global Repair minimum (Cost Settings). Blank = inherit global default (4.5h). 0 = no minimum.">Min Repair Hrs</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={isDepot ? 5 : 6} className="px-3 py-6 text-center text-slate-400 text-sm">No {label.toLowerCase()}s yet.</td></tr>
            ) : items.map((b) => (
              <tr key={b.id} className="border-b border-slate-200 hover:bg-slate-50">
                <td className="px-3 py-2 font-medium">{b.name}</td>
                <td className="px-3 py-2 font-mono">{b.labour_rate}</td>
                <td className="px-3 py-2 font-mono">{b.default_markup_percent ?? 0}%</td>
                {!isDepot && (
                  <td className={`px-3 py-2 text-right font-mono ${Number(b.surcharge_amount || 0) > 0 ? "text-brand-orange font-semibold" : "text-slate-300"}`}>
                    {Number(b.surcharge_amount || 0) > 0 ? `£${Number(b.surcharge_amount).toFixed(2)}` : "—"}
                  </td>
                )}
                <td className={`px-3 py-2 text-right font-mono ${b.min_repair_hours != null ? "text-brand-blue font-semibold" : "text-slate-400"}`}>
                  {b.min_repair_hours != null ? `${b.min_repair_hours}h` : <span className="italic">inherit</span>}
                </td>
                <td className="px-3 py-2 text-right space-x-2">
                  <button onClick={() => setEditing({ ...b, kind })} className="text-slate-500 hover:text-slate-900"><Edit3 size={14} /></button>
                  <button onClick={() => remove(b.id)} className="text-slate-400 hover:text-red-600"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editing && (
        <SimpleDialog title={editing.id ? `Edit ${label}` : `New ${label}`} onClose={() => setEditing(null)} onSave={save}>
          <Input label="Name" value={editing.name} set={(v) => setEditing({ ...editing, name: v })} />
          <Input label="Labour Rate £/hr" type="number" value={editing.labour_rate} set={(v) => setEditing({ ...editing, labour_rate: Number(v) })} />
          <Input label="Default Materials Markup %" type="number" value={editing.default_markup_percent ?? 0} set={(v) => setEditing({ ...editing, default_markup_percent: Number(v) })} />
          {!isDepot && (
            <label className="block">
              <span className="block text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">
                Key-account surcharge (£)
              </span>
              <input
                type="number"
                step="0.01"
                min="0"
                className="qb-input"
                value={editing.surcharge_amount ?? 0}
                onChange={(e) => setEditing({ ...editing, surcharge_amount: Number(e.target.value) })}
                data-testid="key-account-surcharge-input"
                placeholder="0.00"
              />
              <span className="block text-[11px] text-slate-500 mt-1">
                Flat £ amount auto-added to every new quote where this client is picked as the Labour Type (e.g. admin fee).
                Estimators can still override per quote.
              </span>
            </label>
          )}
          <label className="block">
            <span className="block text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">
              Minimum Repair Hours
            </span>
            <input
              type="number"
              step="0.5"
              min="0"
              className="qb-input"
              value={editing.min_repair_hours ?? ""}
              onChange={(e) => setEditing({
                ...editing,
                min_repair_hours: e.target.value === "" ? null : Number(e.target.value),
              })}
              data-testid="min-repair-hours-input"
              placeholder="Leave blank to inherit global default"
            />
            <span className="block text-[11px] text-slate-500 mt-1">
              Overrides the global "Repair minimum hours" cost setting when this {label.toLowerCase()} is picked as the Labour Type.
              Leave blank to inherit the default (currently 4.5h). Set to <b>0</b> for clients / depots
              that allow travel-only or zero-hours Repair jobs (e.g. Lorne Stewart - CCC).
            </span>
          </label>
        </SimpleDialog>
      )}
    </div>
  );
}
